import React, { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Pause } from "lucide-react";

// On-screen d-pad + pause button for touch devices.
// Mutates inputRef in place (same object the keyboard handlers use).
// Auto-shows on touch devices; hidden on desktops with a mouse.
export default function TouchControls({ inputRef, onTogglePause }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show on any coarse pointer device (phones, tablets) or if touch API exists
    const isTouch =
      typeof window !== "undefined" &&
      (("ontouchstart" in window) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        window.matchMedia("(hover: none), (pointer: coarse)").matches);
    setVisible(!!isTouch);
  }, []);

  if (!visible) return null;

  const set = (dir, val) => (e) => {
    // Prevent long-press context menus & scrolling while pressing pad
    e.preventDefault();
    if (inputRef && inputRef.current) inputRef.current[dir] = val;
  };

  const btn =
    "flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-black/60 border border-cyan-300/50 text-cyan-300 " +
    "backdrop-blur-md rounded-xl active:bg-cyan-300 active:text-black transition-colors select-none touch-none";

  return (
    <div className="fixed inset-0 pointer-events-none z-40" data-testid="touch-controls">
      {/* D-pad — bottom-left */}
      <div
        className="absolute left-4 bottom-6 grid grid-cols-3 grid-rows-3 gap-2 pointer-events-auto"
        style={{ touchAction: "none" }}
        data-testid="touch-dpad"
      >
        <div />
        <button
          data-testid="touch-up"
          aria-label="Up"
          onPointerDown={set("up", true)}
          onPointerUp={set("up", false)}
          onPointerCancel={set("up", false)}
          onPointerLeave={set("up", false)}
          className={btn}
        >
          <ChevronUp size={26} />
        </button>
        <div />
        <button
          data-testid="touch-left"
          aria-label="Left"
          onPointerDown={set("left", true)}
          onPointerUp={set("left", false)}
          onPointerCancel={set("left", false)}
          onPointerLeave={set("left", false)}
          className={btn}
        >
          <ChevronLeft size={26} />
        </button>
        <div />
        <button
          data-testid="touch-right"
          aria-label="Right"
          onPointerDown={set("right", true)}
          onPointerUp={set("right", false)}
          onPointerCancel={set("right", false)}
          onPointerLeave={set("right", false)}
          className={btn}
        >
          <ChevronRight size={26} />
        </button>
        <div />
        <button
          data-testid="touch-down"
          aria-label="Down"
          onPointerDown={set("down", true)}
          onPointerUp={set("down", false)}
          onPointerCancel={set("down", false)}
          onPointerLeave={set("down", false)}
          className={btn}
        >
          <ChevronDown size={26} />
        </button>
        <div />
      </div>

      {/* Pause — bottom-right */}
      {onTogglePause && (
        <button
          data-testid="touch-pause"
          aria-label="Pause"
          onClick={(e) => {
            e.preventDefault();
            onTogglePause();
          }}
          className={
            "absolute right-6 bottom-10 pointer-events-auto flex items-center justify-center " +
            "w-16 h-16 md:w-20 md:h-20 bg-black/60 border border-fuchsia-400/60 text-fuchsia-300 " +
            "backdrop-blur-md rounded-full active:bg-fuchsia-400 active:text-black transition-colors"
          }
          style={{ touchAction: "none" }}
        >
          <Pause size={24} />
        </button>
      )}
    </div>
  );
}
